import { useEffect, useMemo, useRef, useState } from "react";
import { ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import { Link } from "react-router-dom";

import { useAuth } from "auth/AuthContext";
import { logoutUser } from "auth/authHelpers";
import DownArrow from "assets/icons/downArrow.svg";

import styles from "./ProfileWidget.module.css";

const ProfileWidget = () => {
    const { instance, activeAccount } = useAuth();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const profileRef = useRef(null);

    const profileInitials = useMemo(() => {
        if (!activeAccount?.name) return '';
        const splitedName = activeAccount?.name.split(" ");
        if (!splitedName || !splitedName.length) return '';

        return `${splitedName[0]?.charAt(0)?.toUpperCase()}${splitedName[1]?.charAt(0)?.toUpperCase()}`;
    }, [activeAccount?.name]);

    const toggleMenu = () => setIsMenuOpen((prev) => !prev);
    const closeMenu = () => setIsMenuOpen(false);

    useEffect(() => {
        if (!isMenuOpen) {
            return undefined;
        }

        const handleClickOutside = (event) => {
            if (!profileRef.current) {
                return;
            }

            if (!profileRef.current.contains(event.target)) {
                setIsMenuOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isMenuOpen]);

    const handleLogout = async () => {
        closeMenu();

        try {
            await logoutUser(instance, activeAccount);
        } catch (error) {
            console.error("Logout failed", error);
        }
    };

    return (
        <div
            className={styles.Profile}
            ref={profileRef}
            data-testid="profile-widget"
        >
            <div className={styles.ProfileInfo}>
                <span className={styles.ProfileName}>
                    Good Morning {activeAccount?.name.split(" ")[0] || "User"}
                </span>
                <span className={styles.ProfileTime}>
                    {new Date().toLocaleTimeString("en-US", {
                        hour: "2-digit",
                        minute: "2-digit",
                        timeZoneName: "short",
                    })}
                </span>
            </div>
            <div
                className={styles.ProfileImage}
                role="img"
                aria-label="Profile"
            >
                <h1 className={styles.ProfileInitials}>
                    {profileInitials}
                </h1>
            </div>
            <button
                type="button"
                className={styles.ProfileButton}
                aria-haspopup="menu"
                aria-expanded={isMenuOpen}
                aria-label="Open profile menu"
                onClick={toggleMenu}
            >
                <img src={DownArrow} alt="Down arrow" />
            </button>

            {isMenuOpen && (
                <div className={styles.Menu} role="menu">
                    <Link
                        to="/profile"
                        className={styles.MenuItem}
                        role="menuitem"
                    >
                        View Profile Details
                    </Link>
                    <button
                        type="button"
                        className={styles.MenuItem}
                        role="menuitem"
                        onClick={handleLogout}
                    >
                        Logout
                    </button>
                </div>
            )}
        </div>
    );
};

export default ProfileWidget;
